/* eslint-disable react/no-array-index-key */
import React from 'react';
import { Download } from '@carbon/icons-react';
import { IconButton } from '@ditioas/web-ui';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import FileIcon from 'components/FileIcon/FileIcon';
import FullscreenLoading from 'components/FullscreenLoading/FullscreenLoading';
import useObjectSearchParams from 'hooks/useObjectSearchParams';
import { localeFormat, standardDateTimeFormat } from 'libs/dateFunctions';
import { getFileUrl } from 'libs/urlFunctions';
import type { SlateChild, SlateNode } from 'model/info-center';

import { useGetPublicInfoMessageById } from '../../InfoCenterAdmin/message-api-hooks';

const renderFormattedText = (textBlocks: SlateNode['children']) =>
  textBlocks.map((textBlock, index) => {
    if (!textBlock.text && !textBlock.children) return null;
    let textClass = '';
    if (textBlock.bold) textClass += ' font-bold';
    if (textBlock.italic) textClass += ' italic';
    if (textBlock.underline) textClass += ' underline';

    if (textBlock.type === 'link' && textBlock.url) {
      return (
        <a
          key={index}
          href={textBlock.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline hover:text-blue-800"
        >
          {textBlock.children?.[0]?.text || 'Link'}
        </a>
      );
    }

    return (
      <span key={index} className={textClass}>
        {textBlock.text}
      </span>
    );
  });

const ReadOnlyMessage = () => {
  const { t } = useTranslation();
  const { messageId } = useParams<{ messageId?: string }>();
  const { xai, xat } = useObjectSearchParams<{ xai?: string; xat?: string }>();
  const { infoMessage } = useGetPublicInfoMessageById(messageId, xai, xat);

  if (!infoMessage) return <FullscreenLoading />;

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="flex w-full max-w-3xl flex-col bg-white p-0">
        <div className="bg-gray-200 px-6 py-4">
          <h1 className="text-xl font-bold">{infoMessage.title}</h1>
        </div>

        <div className="mt-6 mr-2 text-right text-xs">
          {t('InfoCenter.Common.ModifiedAt', {
            date: localeFormat(
              new Date(infoMessage.modifiedAt),
              standardDateTimeFormat
            ),
          })}
        </div>

        <div className="mt-4 w-full bg-white p-6">
          {infoMessage.workDescription?.map((block, index) => {
            if (block.type === 'heading-one') {
              return (
                <h1 key={index} className="mb-4 text-2xl font-bold">
                  {renderFormattedText(block.children)}
                </h1>
              );
            }

            if (block.type === 'heading-two') {
              return (
                <h2 key={index} className="mb-4 text-xl font-semibold">
                  {renderFormattedText(block.children)}
                </h2>
              );
            }

            if (block.type === 'heading-three') {
              return (
                <h3 key={index} className="mb-4 text-lg font-medium underline">
                  {renderFormattedText(block.children)}
                </h3>
              );
            }

            if (block.type === 'paragraph') {
              return (
                <p key={index} className="mb-4">
                  {renderFormattedText(block.children)}
                </p>
              );
            }

            if (block.type === 'image' && block.url) {
              return (
                <img
                  key={index}
                  src={block.url}
                  alt="Attached"
                  className="mt-6 h-auto max-w-full rounded-md"
                />
              );
            }

            if (block.type === 'youtube' && block.url) {
              return (
                <div key={index} className="mt-6">
                  <iframe
                    className="mr-auto h-40 w-80 rounded-md"
                    src={block.url}
                    title="YouTube video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              );
            }

            if (block.type === 'vimeo' && block.url) {
              return (
                <div key={index} className="mt-6">
                  <iframe
                    className="mr-auto h-40 w-80 rounded-md"
                    src={block.url}
                    title="Vimeo video"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              );
            }

            if (block.type === 'bulleted-list') {
              return (
                <ul key={index} className="mb-4 list-disc pl-5">
                  {block.children?.map((listItem: SlateChild, i: number) => (
                    <li key={i}>
                      {renderFormattedText(listItem.children as SlateChild[])}
                    </li>
                  ))}
                </ul>
              );
            }

            if (block.type === 'numbered-list') {
              return (
                <ol key={index} className="mb-4 list-decimal pl-5">
                  {block.children?.map((listItem: SlateChild, i: number) => (
                    <li key={i}>
                      {renderFormattedText(listItem.children as SlateChild[])}
                    </li>
                  ))}
                </ol>
              );
            }

            if (block.type === 'list-item') {
              return (
                <li key={index} className="mb-2">
                  {renderFormattedText(block.children)}
                </li>
              );
            }

            return null;
          })}

          {infoMessage?.infoPageFiles?.length > 0 && (
            <div className="mt-6 flex flex-col gap-2">
              <h2 className="mb-2 text-lg font-semibold">
                {t('General.Documents')}
              </h2>
              {infoMessage.infoPageFiles.map((file, index) => (
                <div
                  key={file.fileReferenceId || index}
                  className="flex items-center justify-between"
                >
                  <div className="align-center flex items-end gap-2">
                    <FileIcon
                      fileExtension={file.extension as string}
                      size={24}
                    />
                    <span className="truncate text-sm">{file.name}</span>
                  </div>

                  <IconButton
                    onClick={() => {
                      window.open(getFileUrl(file.fileReferenceId, 2000) || '');
                    }}
                  >
                    <Download size={24} className="text-primary-600" />
                  </IconButton>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReadOnlyMessage;
